// Word card overlay (docs/project.md §7; Card boards): opens on top of the list without resetting
// the search; ‹ n / N › through the current list order; honest URL /d/{dict}/w/{entry}.
import { useEffect, useMemo, useRef } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import { useLiveQuery } from 'dexie-react-hooks';
import { z } from 'zod';
import { groupByDay, searchEntries } from '@solungo/core/search';
import { api } from '@/api/endpoints';
import { useCard, useCards } from '@/db/hooks';
import { db } from '@/db/db';
import { applyMutationRows } from '@/sync/apply';
import { viewRecorder } from '@/sync/views';
import { useDictionary, useUser } from '@/state/session';
import { useOnline } from '@/state/online';
import { useIsDesktop } from '@/lib/useIsDesktop';
import { CardView } from '@/components/CardView';
import { ChevronLeft, ChevronRight } from '@/components/Icons';
import { Sheet, SheetBar, useCloseOverlay, useOverlayChain } from '@/components/ui';
import { refreshDictionaries } from '@/state/session';
import { retryFill } from '@/state/pending';
import { reportError } from '@/lib/errors';
import { track } from '@/lib/analytics';

export const Route = createFileRoute('/_app/d/$dictionaryId/w/$entryId/')({
  validateSearch: z.object({ q: z.string().optional() }),
  component: CardScreen,
});

function CardScreen() {
  const { dictionaryId, entryId } = Route.useParams();
  const { q } = Route.useSearch();
  const t = useTranslations('card');
  const tc = useTranslations('common');
  const navigate = useNavigate();
  const online = useOnline();
  const desktop = useIsDesktop();
  const user = useUser();
  const dictionary = useDictionary(dictionaryId);
  const cards = useCards(dictionary);
  const data = useCard(dictionary, entryId);
  // Cards opened from related words are pushed on top of this one; close skips the whole chain
  const chain = useOverlayChain();
  const close = useCloseOverlay({ to: '/d/$dictionaryId', params: { dictionaryId } }, chain + 1);
  // A deep link on a cold start: the entry is "not found" only once the first pull has completed
  const synced = useLiveQuery(() => db.sync_state.get(dictionaryId), [dictionaryId], undefined);

  // The browsing order: the search results when opened from the omnibox, else the list by day
  const order = useMemo(() => {
    if (!cards || !dictionary) return [];
    if (q) return searchEntries(q, cards, { targetLang: dictionary.targetLang, baseLang: dictionary.baseLang }).map((c) => c.id);
    return groupByDay(cards).flatMap((s) => s.items.map((c) => c.id));
  }, [cards, dictionary, q]);
  const index = order.indexOf(entryId);
  const headwordIndex = useMemo(() => new Map((cards ?? []).map((c) => [c.headwordLower, c.id])), [cards]);

  useEffect(() => {
    void viewRecorder.record(dictionaryId, entryId).then((recorded) => {
      if (recorded && navigator.onLine) void viewRecorder.flush();
    });
  }, [dictionaryId, entryId]);

  // One card_viewed per opened entry, once its word is known (a deep link waits for the sync)
  const viewed = useRef('');
  const headword = data?.card.headword;
  const state = !data
    ? null
    : data.card.filled
      ? data.card.detailsPending
        ? 'details'
        : 'ready'
      : data.card.fillFailed
        ? 'failed'
        : 'building';
  useEffect(() => {
    if (!headword || !state || viewed.current === entryId) return;
    viewed.current = entryId;
    track('card_viewed', { entry_id: entryId, headword, state });
  }, [entryId, headword, state]);

  const go = (delta: number) => {
    const next = order[index + delta];
    if (!next) return;
    track('card_browsed', { direction: delta < 0 ? 'prev' : 'next', from_headword: data?.card.headword ?? '' });
    void navigate({ to: '/d/$dictionaryId/w/$entryId', params: { dictionaryId, entryId: next }, search: { q }, replace: true, state: true });
  };
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'ArrowRight') go(1);
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, order]);

  if (!dictionary) return null;
  // Who added the word: a member's name (owner or teacher); never an e-mail
  const by = data?.card.createdBy;
  const author = by ? dictionary.members.find((m) => m.id === by) : undefined;
  const authorName = author ? (author.name ?? (author.id === dictionary.owner.id ? null : t('teacher'))) : null;

  const onDelete = async () => {
    if (!data || !window.confirm(t('deleteConfirm', { word: data.card.headword }))) return;
    try {
      const res = await api.entries.remove(entryId);
      await applyMutationRows(db, { entry: res.entry });
      track('entry_deleted', { entry_id: entryId, headword: data.card.headword });
      void refreshDictionaries();
      close();
    } catch (e) {
      // the row stays; a retry is one tap away
      reportError(e);
    }
  };

  const nav = order.length > 0 && (
    <div className="flex items-center gap-0.5">
      <button type="button" aria-label={t('prev')} className="icon-btn text-accent disabled:opacity-30" disabled={index <= 0} onClick={() => go(-1)}>
        <ChevronLeft />
      </button>
      <span className="min-w-[56px] text-center text-[15px] font-semibold tabular-nums text-ink-secondary desktop:text-[13px]">
        {index >= 0 ? `${index + 1} / ${order.length}` : ''}
      </span>
      <button type="button" aria-label={t('next')} className="icon-btn text-accent disabled:opacity-30" disabled={index < 0 || index >= order.length - 1} onClick={() => go(1)}>
        <ChevronRight size={22} />
      </button>
    </div>
  );

  return (
    <Sheet onClose={close} label={t('title', { word: data?.card.headword ?? '' })} tone="surface" width="max-w-[940px]" desktopHeight="desktop:h-[min(680px,calc(100vh-80px))]">
      <div className="desktop:border-b desktop:border-separator">
        <SheetBar onClose={close} title={nav} closeLabel={t('title', { word: data?.card.headword ?? '' })} right={<div className="w-11" />} />
      </div>
      {/* keyed by the entry: another card in the same sheet starts scrolled to the top */}
      <div key={entryId} className="flex min-h-0 flex-grow flex-col overflow-y-auto desktop:overflow-hidden" data-testid="card">
        {data ? (
          <CardView
            card={data.card}
            dictionary={dictionary}
            user={user}
            authorName={authorName}
            headwordIndex={headwordIndex}
            online={online}
            desktop={desktop}
            chain={chain}
            onEdit={() => void navigate({ to: '/d/$dictionaryId/w/$entryId/edit', params: { dictionaryId, entryId } })}
            onDelete={() => void onDelete()}
            onRetryFill={() => void retryFill(entryId)}
          />
        ) : data === null && synced ? (
          <div className="p-6 text-[15px] text-ink-secondary">{t('notFound')}</div>
        ) : data === null ? (
          <div className="p-6 text-[15px] text-ink-secondary">{tc('loading')}</div>
        ) : null}
      </div>
    </Sheet>
  );
}
